import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Anchor, GitCompare, X, Hourglass, MapPin } from 'lucide-react';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';

/**
 * Compare tray: a sticky bar that collects up to four trips, plus the side-by-side
 * comparison slide-out. Everything shown here is data we already hold on the activity,
 * so nothing extra is fetched.
 */
const ROWS = [
  { label: 'Charter company', get: (a) => a.seller_name || '\u2014' },
  { label: 'From', get: (a) => (a.price_from ? `$${a.price_from}` : 'Book direct') },
  { label: 'Duration', get: (a) => a.duration || '\u2014' },
  { label: 'Location', get: (a) => a.location || '\u2014' },
  { label: 'Category', get: (a) => (a.category_names || []).join(', ') || '\u2014' },
  { label: 'Tags', get: (a) => (a.tags || []).join(', ') || '\u2014' },
];

const ActivityCompareTray = ({ items, onRemove, onClear, onBook, describe }) => {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur" data-testid="activity-compare-tray">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
            <GitCompare className="h-4 w-4" /> Comparing {items.length}/4
          </span>

          <div className="flex flex-1 flex-wrap items-center gap-2">
            {items.map((a) => (
              <span key={a.id} className="inline-flex max-w-[220px] items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 py-1 pl-1 pr-2 text-xs font-semibold text-slate-700">
                {a.images?.[0] && <img src={resolveMediaUrl(a.images[0])} alt="" className="h-6 w-6 rounded-full object-cover" />}
                <span className="truncate">{a.title}</span>
                <button type="button" onClick={() => onRemove(a)} aria-label={`Remove ${a.title}`} className="text-slate-400 hover:text-red-600" data-testid={`activity-compare-remove-${a.alias}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>

          <button type="button" onClick={onClear} className="text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="activity-compare-clear">
            Clear
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            disabled={items.length < 2}
            className="rounded-sm bg-blue-800 px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="activity-compare-open"
          >
            Compare Trips
          </button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto bg-white border-slate-200 text-slate-900" data-testid="activity-compare-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-slate-900">
              <GitCompare className="h-5 w-5 text-blue-800" /> Compare {items.length} trips
            </SheetTitle>
          </SheetHeader>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="w-32" />
                  {items.map((a) => (
                    <th key={a.id} className="border-l border-gray-200 p-3 align-top text-left">
                      {a.images?.[0] && (
                        <img src={resolveMediaUrl(a.images[0])} alt={a.title} className="mb-2 h-24 w-full rounded object-cover" />
                      )}
                      <span className="block font-bold text-gray-900">{a.title}</span>
                      {a.seller_slug && (
                        <Link to={`/${a.seller_slug}`} className="mt-0.5 block text-xs font-semibold text-blue-800 hover:underline">
                          {a.seller_name}
                        </Link>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.label} className="border-t border-gray-200">
                    <th className="p-3 text-left align-top text-xs font-bold uppercase tracking-wide text-slate-500">{row.label}</th>
                    {items.map((a) => (
                      <td key={a.id} className="border-l border-gray-200 p-3 align-top text-slate-700">{row.get(a)}</td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-gray-200">
                  <th className="p-3 text-left align-top text-xs font-bold uppercase tracking-wide text-slate-500">Details</th>
                  {items.map((a) => (
                    <td key={a.id} className="border-l border-gray-200 p-3 align-top text-slate-600">
                      <span className="line-clamp-6 block">{describe(a) || '\u2014'}</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-gray-200">
                  <th className="p-3" />
                  {items.map((a) => (
                    <td key={a.id} className="border-l border-gray-200 p-3 align-top">
                      <button
                        type="button"
                        onClick={(e) => { setOpen(false); onBook(a, e); }}
                        className="flex w-full items-center justify-center gap-2 rounded-sm bg-blue-800 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-blue-900"
                        data-testid={`activity-compare-book-${a.alias}`}
                      >
                        <Anchor className="h-3.5 w-3.5" /> Book Now
                      </button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <Hourglass className="h-3.5 w-3.5" /> Duration and <MapPin className="h-3.5 w-3.5" /> location come straight from each charter company&rsquo;s listing.
          </p>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ActivityCompareTray;
